import { useState, useMemo } from 'react';
import { ArrowLeft, ArrowRight, Check, X, Building2, Warehouse, Tag } from 'lucide-react';
import { useEslPlatformEnabled } from '@/hooks/use-feature-flags';
import { useSignup } from '@/hooks/use-auth';
import { PasswordInput } from '@/components/ui/password-input';
import { PhoneInput } from '@/components/ui/phone-input';
import { AuthLayout } from '@/components/layout/auth-layout';
import { US_STATES } from '@/lib/constants';
import { validatePhone } from '@/lib/validators';
import { useBrandingStore } from '@/stores/branding-store';

const inputBase = 'h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1';
const inputNormal = `${inputBase} border-input focus:border-primary focus:ring-primary`;
const inputError = `${inputBase} border-red-500 focus:border-red-500 focus:ring-red-500`;

// ── Password Policy Rules ──
const PASSWORD_RULES = [
  { key: 'minLength', label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { key: 'uppercase', label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { key: 'lowercase', label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { key: 'digit', label: 'One numeric digit', test: (p: string) => /\d/.test(p) },
  { key: 'special', label: 'One special character (!@#$%...)', test: (p: string) => /[^A-Za-z0-9]/.test(p) },
] as const;

function PasswordChecklist({ password }: { password: string }) {
  if (!password) return null;

  return (
    <ul className="mt-2 space-y-1">
      {PASSWORD_RULES.map((rule) => {
        const passed = rule.test(password);
        return (
          <li key={rule.key} className="flex items-center gap-1.5 text-xs">
            {passed ? <Check className="h-3 w-3 text-green-600" /> : <X className="h-3 w-3 text-red-400" />}
            <span className={passed ? 'text-green-600' : 'text-muted-foreground'}>{rule.label}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function SignupPage() {
  const signup = useSignup();
  const { config: branding } = useBrandingStore();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Step 1 fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Step 2 fields
  const [businessType, setBusinessType] = useState<'company' | 'warehouse' | 'esl'>('company');
  // Platform release switch. While shelf labels are off the account kind is not
  // offered at all, and the API refuses it too — this is presentation of a
  // server-side rule, not the rule itself.
  const eslEnabled = useEslPlatformEnabled();
  const [businessName, setBusinessName] = useState('');
  const [phone, setPhone] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [country] = useState('United States');

  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const allPasswordRulesPassed = useMemo(
    () => password.length > 0 && PASSWORD_RULES.every((rule) => rule.test(password)),
    [password],
  );

  const validateStep1 = () => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = 'Name is required';
    if (!email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Please enter a valid email';
    if (!password) errors.password = 'Password is required';
    else if (!allPasswordRulesPassed) errors.password = 'Password does not meet all requirements';
    if (!confirmPassword) errors.confirmPassword = 'Confirm password is required';
    else if (password && confirmPassword && password !== confirmPassword)
      errors.confirmPassword = 'Passwords do not match';
    return errors;
  };

  const validateStep2 = () => {
    const errors: Record<string, string> = {};
    if (!businessName.trim()) errors.businessName = 'Business name is required';
    const phoneErr = validatePhone(phone, true);
    if (phoneErr) errors.phone = phoneErr;
    if (!street.trim()) errors.street = 'Street address is required';
    if (!city.trim()) errors.city = 'City is required';
    if (!state.trim()) errors.state = 'State is required';
    if (!zip.trim()) errors.zip = 'Zip code is required';
    else if (zip.length !== 5) errors.zip = 'Zip code must be 5 digits';
    if (!country.trim()) errors.country = 'Country is required';
    return errors;
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const errors = validateStep1();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const errors = validateStep2();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      await signup.mutateAsync({
        businessName,
        name,
        email,
        password,
        phone,
        street,
        city,
        state,
        zip,
        country,
        type: businessType,
      });
      window.location.hash = '#/';
    } catch (err: any) {
      setError(err?.error?.message || 'Signup failed');
    }
  };

  return (
    <AuthLayout>
      <div className="w-full max-w-sm lg:max-w-[430px] space-y-6 rounded-xl border border-border bg-white/80 p-8 shadow-sm backdrop-blur-sm">
        <div className="text-center">
          <img
            src={branding.logoUrl || '/logo.png'}
            alt={branding.appName || 'Q PaymentZ'}
            className="mx-auto h-10 w-auto sm:h-12 lg:h-14 object-contain"
          />
          <p className="mt-3 text-[18px] text-muted-foreground">
            {step === 1 ? 'Create your account' : 'Business Information'}
          </p>
          <div className="mt-3 flex justify-center gap-2">
            <div className={`h-1.5 w-12 rounded-full ${step >= 1 ? 'bg-primary' : 'bg-gray-200'}`} />
            <div className={`h-1.5 w-12 rounded-full ${step >= 2 ? 'bg-primary' : 'bg-gray-200'}`} />
          </div>
        </div>

        {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

        {step === 1 && (
          <form onSubmit={handleNext} noValidate className="space-y-4">
            <div>
              <label className="mb-1 block text-[17px] font-medium text-[#2c3345]">Your Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^A-Za-z\s]/g, '');
                  setName(value);
                  clearFieldError('name');
                }}
                autoFocus
                className={fieldErrors.name ? inputError : inputNormal}
                placeholder="John Doe"
              />
              {fieldErrors.name && <p className="mt-1 text-xs text-red-500">{fieldErrors.name}</p>}
            </div>

            <div>
              <label className="mb-1 block text-[17px] font-medium text-[#2c3345]">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearFieldError('email');
                }}
                className={fieldErrors.email ? inputError : inputNormal}
                placeholder="you@example.com"
              />
              {fieldErrors.email && <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>}
            </div>

            <div>
              <label className="mb-1 block text-[17px] font-medium text-[#2c3345]">Password</label>
              <PasswordInput
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clearFieldError('password');
                }}
                className={fieldErrors.password ? inputError : inputNormal}
                placeholder="At least 8 characters"
              />
              {fieldErrors.password && <p className="mt-1 text-xs text-red-500">{fieldErrors.password}</p>}
              <PasswordChecklist password={password} />
            </div>

            <div>
              <label className="mb-1 block text-[17px] font-medium text-[#2c3345]">Confirm Password</label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  clearFieldError('confirmPassword');
                }}
                className={fieldErrors.confirmPassword ? inputError : inputNormal}
                placeholder="Repeat your password"
              />
              {fieldErrors.confirmPassword && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.confirmPassword}</p>
              )}
            </div>

            <button
              type="submit"
              className="flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primary text-[17px] font-bold text-primary-foreground hover:bg-primary/90"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {/* Business Type Selector */}
            <div>
              <label className="mb-1.5 block text-[17px] font-medium text-[#2c3345]">Business Type</label>
              {/* One per row. Three side-by-side columns in a panel this narrow
                  squeeze each card to about 90px, which wraps every label one
                  word per line and pushes the icons out of alignment. */}
              <div className="grid gap-2">
                <button
                  type="button"
                  onClick={() => setBusinessType('company')}
                  className={`flex w-full items-center gap-2.5 rounded-lg border-2 p-2.5 text-left text-sm transition-colors ${
                    businessType === 'company'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/30'
                  }`}
                >
                  <Building2
                    className={`h-4 w-4 shrink-0 ${businessType === 'company' ? 'text-primary' : 'text-muted-foreground'}`}
                  />
                  <div>
                    <div className="font-medium">Company</div>
                    <div className="text-[11px] leading-snug text-muted-foreground">
                      Retail, restaurant, startup, non-profit etc.
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setBusinessType('warehouse')}
                  className={`flex w-full items-center gap-2.5 rounded-lg border-2 p-2.5 text-left text-sm transition-colors ${
                    businessType === 'warehouse'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-border hover:border-muted-foreground/30'
                  }`}
                >
                  <Warehouse
                    className={`h-4 w-4 shrink-0 ${businessType === 'warehouse' ? 'text-orange-600' : 'text-muted-foreground'}`}
                  />
                  <div>
                    <div className="font-medium">Warehouse</div>
                    <div className="text-[11px] leading-snug text-muted-foreground">
                      Warehouse, vendor, manufacturer, distributor.
                    </div>
                  </div>
                </button>
                {/* Shelf labels only. This account never collects payments, so
                    signup asks for a business name and address and nothing
                    more — no EIN, no bank details, no identity documents. */}
                {eslEnabled && (
                  <button
                    type="button"
                    onClick={() => setBusinessType('esl')}
                    className={`flex w-full items-center gap-2.5 rounded-lg border-2 p-2.5 text-left text-sm transition-colors ${
                      businessType === 'esl'
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-border hover:border-muted-foreground/30'
                    }`}
                  >
                    <Tag
                      className={`h-4 w-4 shrink-0 ${businessType === 'esl' ? 'text-teal-600' : 'text-muted-foreground'}`}
                    />
                    <div>
                      <div className="font-medium">Shelf Labels Only</div>
                      <div className="text-[11px] text-muted-foreground">
                        You run your own POS, or just want digital shelf labels.
                      </div>
                    </div>
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-[17px] font-medium text-[#2c3345]">
                {businessType === 'warehouse' ? 'Warehouse' : 'Business'} Name
              </label>
              <input
                type="text"
                value={businessName}
                onChange={(e) => {
                  setBusinessName(e.target.value);
                  clearFieldError('businessName');
                }}
                autoFocus
                className={fieldErrors.businessName ? inputError : inputNormal}
                placeholder="My Business LLC"
              />
              {fieldErrors.businessName && <p className="mt-1 text-xs text-red-500">{fieldErrors.businessName}</p>}
            </div>

            <div>
              <label className="mb-1 block text-[17px] font-medium text-[#2c3345]">Phone Number</label>
              <PhoneInput
                value={phone}
                onValueChange={(digits) => {
                  setPhone(digits);
                  clearFieldError('phone');
                }}
                className={fieldErrors.phone ? inputError : inputNormal}
                placeholder="123-456-7890"
              />
              {fieldErrors.phone && <p className="mt-1 text-xs text-red-500">{fieldErrors.phone}</p>}
            </div>

            <div>
              <label className="mb-1 block text-[17px] font-medium text-[#2c3345]">Street Address</label>
              <input
                type="text"
                value={street}
                onChange={(e) => {
                  setStreet(e.target.value);
                  clearFieldError('street');
                }}
                className={fieldErrors.street ? inputError : inputNormal}
                placeholder="123 Main St"
              />
              {fieldErrors.street && <p className="mt-1 text-xs text-red-500">{fieldErrors.street}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-[17px] font-medium text-[#2c3345]">City</label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value.replace(/[^A-Za-z\s]/g, ''));
                    clearFieldError('city');
                  }}
                  className={fieldErrors.city ? inputError : inputNormal}
                  placeholder="San Francisco"
                />
                {fieldErrors.city && <p className="mt-1 text-xs text-red-500">{fieldErrors.city}</p>}
              </div>
              <div>
                <label className="mb-1 block text-[17px] font-medium text-[#2c3345]">State</label>
                <select
                  value={state}
                  onChange={(e) => {
                    setState(e.target.value);
                    clearFieldError('state');
                  }}
                  className={fieldErrors.state ? inputError : inputNormal}
                >
                  <option value="">Select</option>
                  {US_STATES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                {fieldErrors.state && <p className="mt-1 text-xs text-red-500">{fieldErrors.state}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-[17px] font-medium text-[#2c3345]">Zip Code</label>
                <input
                  type="text"
                  value={zip}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setZip(digits);
                    clearFieldError('zip');
                  }}
                  className={fieldErrors.zip ? inputError : inputNormal}
                  placeholder="94102"
                  maxLength={5}
                  inputMode="numeric"
                />
                {fieldErrors.zip && <p className="mt-1 text-xs text-red-500">{fieldErrors.zip}</p>}
              </div>
              <div>
                <label className="mb-1 block text-[17px] font-medium text-[#2c3345]">Country</label>
                <input type="text" value={country} readOnly className={`${inputNormal} bg-muted cursor-not-allowed`} />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex h-10 flex-1 items-center justify-center gap-2 rounded-lg border border-border text-[17px] font-bold text-foreground hover:bg-muted"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              <button
                type="submit"
                disabled={signup.isPending}
                className="h-10 flex-1 rounded-lg bg-primary text-[17px] font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {signup.isPending ? 'Creating...' : 'Create Account'}
              </button>
            </div>
          </form>
        )}

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <a href="#/login" className="font-medium text-primary hover:underline">
            Sign In
          </a>
        </p>
      </div>
    </AuthLayout>
  );
}
